import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { getTheme } from '@/components/ThemeCustomizer';
import { ChevronRight, ChevronLeft, Printer, Plus, Check, X, Wallet, Trash2, Pencil, Building2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { translateSubType } from '@/utils/transactionTypes';
import { WORKFLOW_STEPS } from '@/utils/workflowSteps';
import { TxDocuments } from '@/components/TxDocuments';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColor = { completed: '#059669', pending: '#d97706', executing: '#0284c7', waiting_approval: '#7c3aed', done_unpaid: '#6366f1', cancelled: '#dc2626' };
const statusLabelMap = (s, isRTL) => {
  const map = { completed: isRTL ? 'مكتمل' : 'Completed', pending: isRTL ? 'قيد الإجراء' : 'Pending', executing: isRTL ? 'جاري' : 'Executing', waiting_approval: isRTL ? 'بانتظار' : 'Waiting', cancelled: isRTL ? 'ملغي · مسترد' : 'Cancelled · refunded' };
  return map[s] || s;
};

const AllPlansImage = ({ isRTL, plans, headers, onClose, isAdmin, onCreatePlan, onRefresh, fixedClientId }) => {
  const captureRef = useRef(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportData, setExportData] = useState(null);

  // Date filter
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(now.getMonth());
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Inline add plan - state at top level to avoid re-mount focus loss
  const [showAddForm, setShowAddForm] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [empDropdownOpen, setEmpDropdownOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [formMainType, setFormMainType] = useState('');
  const [selectedSteps, setSelectedSteps] = useState({}); // { sub_type: price }
  const [submitting, setSubmitting] = useState(false);
  const [empPriceList, setEmpPriceList] = useState({});

  useEffect(() => {
    const compId = selectedEmp?.company_id || selectedEmp?.establishment_id;
    if (!compId) { setEmpPriceList({}); return; }
    axios.get(`${API}/companies/${compId}/prices`, { headers }).then(r => setEmpPriceList(r.data.prices || {})).catch(() => setEmpPriceList({}));
  }, [selectedEmp?.id]);

  const updateEntryDocuments = (txId, documents) => {
    setExportData(prev => prev ? { ...prev, ledger: prev.ledger.map(e => e.id === txId ? { ...e, documents } : e) } : prev);
  };

  // Deposit form
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositNotes, setDepositNotes] = useState('');
  const [depositDate, setDepositDate] = useState(new Date().toISOString().slice(0, 10));
  const [depositSubmitting, setDepositSubmitting] = useState(false);

  // Inline edit (for transactions)
  const [editingId, setEditingId] = useState(null);
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Inline edit deposit
  const [editingDepositId, setEditingDepositId] = useState(null);
  const [editDepositAmount, setEditDepositAmount] = useState('');

  // Company transaction form
  const [showCompanyTxForm, setShowCompanyTxForm] = useState(false);
  const [clientCompanies, setClientCompanies] = useState([]);
  const [compTxCompanyId, setCompTxCompanyId] = useState('');
  const [compTxName, setCompTxName] = useState('');
  const [compTxPrice, setCompTxPrice] = useState('');
  const [compTxDate, setCompTxDate] = useState(new Date().toISOString().slice(0, 10));
  const [compTxSubmitting, setCompTxSubmitting] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await axios.get(`${API}/clients`, { headers });
        const allClients = res.data || [];
        if (fixedClientId) {
          const own = allClients.find(c => c.id === fixedClientId);
          setClients(own ? [own] : []);
          setSelectedClient(own || null);
        } else {
          const clientIds = [...new Set(plans.map(p => p.client_id).filter(Boolean))];
          setClients(allClients.filter(c => clientIds.includes(c.id)));
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchClients();
  }, [plans]);

  const getDateRange = () => {
    if (useCustomRange && customStart && customEnd) return { start: customStart, end: customEnd };
    const start = `${filterYear}-${String(filterMonth + 1).padStart(2, '0')}-01`;
    const endMonth = filterMonth === 11 ? 0 : filterMonth + 1;
    const endYear = filterMonth === 11 ? filterYear + 1 : filterYear;
    const end = `${endYear}-${String(endMonth + 1).padStart(2, '0')}-01`;
    return { start, end };
  };

  const monthNames = isRTL
    ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const fetchExportData = async () => {
    if (!selectedClient) { setExportData(null); return; }
    try {
      const { start, end } = getDateRange();
      const res = await axios.get(`${API}/clients/${selectedClient.id}/export-data?start_date=${start}&end_date=${end}`, { headers });
      setExportData(res.data);
    } catch { setExportData(null); }
  };

  useEffect(() => { fetchExportData(); }, [selectedClient, filterMonth, filterYear, useCustomRange, customStart, customEnd]);

  useEffect(() => {
    if (!selectedClient) return;
    const fetchClientData = async () => {
      try {
        const compRes = await axios.get(`${API}/companies?client_id=${selectedClient.id}`, { headers });
        const comps = compRes.data || [];
        setClientCompanies(comps);
        const compIds = comps.map(c => c.id);
        const empRes = await axios.get(`${API}/employees`, { headers });
        const allEmps = empRes.data || [];
        setEmployees(allEmps.filter(e => compIds.includes(e.company_id) || compIds.includes(e.establishment_id)));
      } catch (e) { console.error(e); }
    };
    fetchClientData();
  }, [selectedClient]);

  const filteredEmps = useMemo(() => {
    if (!empSearch.trim()) return employees;
    const q = empSearch.trim().toLowerCase();
    return employees.filter(e => e.name?.toLowerCase().includes(q));
  }, [employees, empSearch]);

  const handlePrint = () => {
    if (!captureRef.current) return;
    const pw = window.open('', '_blank');
    if (!pw) { toast.error(isRTL ? 'يرجى السماح بالنوافذ المنبثقة' : 'Please allow popups'); return; }
    const theme = getTheme();
    pw.document.write(`<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}" lang="${isRTL ? 'ar' : 'en'}"><head><meta charset="utf-8"><title>${selectedClient.name}</title><style>
      :root { --theme-primary: ${theme.primary}; --theme-accent: ${theme.accent}; }
      @page { size: A4 landscape; margin: 8mm 8mm 10mm; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      html, body { width: 100%; background: #fff; }
      body { font-family: system-ui, -apple-system, 'Segoe UI', Tahoma, sans-serif; font-size: 10px; color: #0f172a; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      table { width: 100% !important; border-collapse: collapse; table-layout: fixed; page-break-inside: auto; font-size: 10px !important; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; break-inside: avoid; }
      th, td { padding: 4px 6px !important; word-wrap: break-word; overflow-wrap: anywhere; vertical-align: middle; }
      th { font-size: 10px !important; }
      tbody tr:nth-child(even) td { background-color: #f8fafc; }
      .no-print, button { display: none !important; }
      .print-only { display: block !important; }
      select { appearance: none; -webkit-appearance: none; border: none !important; background: transparent !important; }
      img { max-width: 100%; }
    </style></head><body>${captureRef.current.innerHTML}</body></html>`);
    pw.document.close();
    pw.onload = () => { setTimeout(() => pw.print(), 150); };
  };

  const toggleStep = (step) => {
    setSelectedSteps(prev => {
      const next = { ...prev };
      if (next[step.sub_type] !== undefined) delete next[step.sub_type];
      else next[step.sub_type] = empPriceList?.[formMainType]?.[step.sub_type] || 0;
      return next;
    });
  };

  const availableSteps = formMainType && WORKFLOW_STEPS[formMainType] ? WORKFLOW_STEPS[formMainType].steps : [];

  // Submit plan
  const handleAddPlan = async () => {
    if (!selectedEmp) { toast.error(isRTL ? 'اختر الموظف' : 'Select employee'); return; }
    const stepsToAdd = Object.entries(selectedSteps);
    if (stepsToAdd.length === 0) { toast.error(isRTL ? 'اختر معاملة واحدة على الأقل' : 'Select at least one step'); return; }
    setSubmitting(true);
    try {
      const wf = WORKFLOW_STEPS[formMainType];
      const steps = stepsToAdd.map(([subType, price]) => {
        const found = wf?.steps.find(s => s.sub_type === subType);
        return { sub_type: subType, label_ar: found?.ar || subType, label_en: found?.en || subType, main_type: formMainType, price };
      });
      await axios.post(`${API}/work-plans`, { employee_id: selectedEmp.id, employee_ids: [selectedEmp.id], plan_type: 'employee', steps, notes: '', execute_mode: 'on_account' }, { headers });
      toast.success(isRTL ? 'تم إضافة الخطة' : 'Plan added');
      resetAddForm();
      if (onRefresh) await onRefresh();
      await fetchExportData();
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ' : 'Error')); }
    setSubmitting(false);
  };

  const resetAddForm = () => {
    setSelectedEmp(null); setEmpSearch(''); setFormMainType(''); setSelectedSteps({}); setShowAddForm(false);
  };

  // Submit deposit
  const handleAddDeposit = async () => {
    const amt = parseFloat(depositAmount);
    if (!amt || amt <= 0) { toast.error(isRTL ? 'أدخل مبلغ صحيح' : 'Enter valid amount'); return; }
    setDepositSubmitting(true);
    try {
      await axios.post(`${API}/deposits`, {
        client_id: selectedClient.id, amount: amt,
        deposit_date: depositDate ? depositDate + 'T00:00:00' : new Date().toISOString(),
        notes: depositNotes || '',
      }, { headers });
      toast.success(isRTL ? 'تم إضافة الرصيد' : 'Deposit added');
      setDepositAmount(''); setDepositNotes(''); setDepositDate(new Date().toISOString().slice(0, 10)); setShowDepositForm(false);
      if (onRefresh) await onRefresh();
      await fetchExportData();
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ' : 'Error')); }
    setDepositSubmitting(false);
  };

  // Submit company transaction
  const handleAddCompanyTx = async () => {
    if (!compTxCompanyId) { toast.error(isRTL ? 'اختر المنشأة' : 'Select company'); return; }
    if (!compTxName.trim()) { toast.error(isRTL ? 'أدخل اسم المعاملة' : 'Enter transaction name'); return; }
    const price = parseFloat(compTxPrice);
    if (!price || price <= 0) { toast.error(isRTL ? 'أدخل مبلغ صحيح' : 'Enter valid amount'); return; }
    setCompTxSubmitting(true);
    try {
      await axios.post(`${API}/company-transactions`, {
        company_id: compTxCompanyId,
        name: compTxName.trim(),
        price: price,
        actual_paid_amount: 0,
        work_date: compTxDate ? compTxDate + 'T00:00:00' : new Date().toISOString(),
        status: 'pending',
      }, { headers });
      toast.success(isRTL ? 'تم إضافة معاملة المنشأة' : 'Company transaction added');
      setCompTxCompanyId(''); setCompTxName(''); setCompTxPrice(''); setCompTxDate(new Date().toISOString().slice(0, 10)); setShowCompanyTxForm(false);
      if (onRefresh) await onRefresh();
      await fetchExportData();
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ' : 'Error')); }
    setCompTxSubmitting(false);
  };

  // Delete transaction
  const handleDeleteTransaction = async (transId, isCompanyTx) => {
    if (!window.confirm(isRTL ? 'هل تريد حذف هذه المعاملة؟' : 'Delete this transaction?')) return;
    try {
      const endpoint = isCompanyTx ? `${API}/company-transactions/${transId}` : `${API}/sub-transactions/${transId}`;
      await axios.delete(endpoint, { headers });
      toast.success(isRTL ? 'تم الحذف' : 'Deleted');
      if (onRefresh) await onRefresh();
      await fetchExportData();
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ' : 'Error')); }
  };

  // Update company transaction status (single place: Work Plans ledger)
  const handleUpdateCompanyTxStatus = async (txId, newStatus) => {
    try {
      if (newStatus === 'cancelled') {
        if (!window.confirm(isRTL ? 'إلغاء معاملة المنشأة؟ سيتم حذفها نهائياً من كل الأماكن ويُعاد المبلغ لرصيد العميل تلقائياً.' : 'Cancel this company transaction? It will be permanently removed everywhere and the client balance restored.')) return;
        await axios.put(`${API}/company-transactions/${txId}/cancel`, {}, { headers });
        toast.success(isRTL ? 'تم إلغاء المعاملة وحذفها من كل مكان' : 'Cancelled & removed everywhere');
      } else {
        const payload = { status: newStatus };
        if (newStatus === 'completed') {
          const entry = (exportData?.ledger || []).find(e => e.id === txId);
          const cur = entry?.actual_paid_amount;
          const val = window.prompt(isRTL ? 'المبلغ الفعلي المدفوع (حتى لو 0):' : 'Actual paid amount (even if 0):', cur != null ? String(cur) : '0');
          if (val === null) return;
          const num = parseFloat(val);
          if (isNaN(num) || num < 0) { toast.error(isRTL ? 'مبلغ غير صالح' : 'Invalid amount'); return; }
          payload.actual_paid_amount = num;
        }
        await axios.put(`${API}/company-transactions/${txId}`, payload, { headers });
        toast.success(isRTL ? 'تم تحديث الحالة' : 'Status updated');
      }
      if (onRefresh) await onRefresh();
      await fetchExportData();
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ' : 'Error')); }
  };

  // Delete deposit
  const handleDeleteDeposit = async (depId) => {
    if (!window.confirm(isRTL ? 'هل تريد حذف هذا الإيداع؟' : 'Delete this deposit?')) return;
    try {
      await axios.delete(`${API}/deposits/${depId}`, { headers });
      toast.success(isRTL ? 'تم الحذف' : 'Deleted');
      if (onRefresh) await onRefresh();
      await fetchExportData();
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ' : 'Error')); }
  };

  // Edit deposit amount
  const handleSaveDepositAmount = async (depId) => {
    const amt = parseFloat(editDepositAmount);
    if (!amt || amt <= 0) { toast.error(isRTL ? 'مبلغ غير صحيح' : 'Invalid amount'); return; }
    try {
      await axios.put(`${API}/deposits/${depId}`, { amount: amt }, { headers });
      toast.success(isRTL ? 'تم الحفظ' : 'Saved');
      setEditingDepositId(null); setEditDepositAmount('');
      if (onRefresh) await onRefresh();
      await fetchExportData();
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ' : 'Error')); }
  };

  // Inline edit transaction
  const handleEditSave = async (entryId) => {
    if (!entryId || !editField) return;
    try {
      const payload = {};
      if (editField === 'price') payload.price = parseFloat(editValue) || 0;
      if (editField === 'date') payload.work_date = editValue;
      const isCompanyTx = (exportData?.ledger || []).some(e => e.id === entryId && e.is_company_tx);
      await axios.put(`${API}/${isCompanyTx ? 'company-transactions' : 'sub-transactions'}/${entryId}`, payload, { headers });
      toast.success(isRTL ? 'تم الحفظ' : 'Saved');
      setEditingId(null); setEditField(null); setEditValue('');
      if (onRefresh) await onRefresh();
      await fetchExportData();
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ' : 'Error')); }
  };

  const startEdit = (id, field, currentValue) => {
    setEditingId(id); setEditField(field);
    setEditValue(field === 'date' && currentValue ? new Date(currentValue).toISOString().slice(0, 10) : String(currentValue || ''));
  };

  // ============ LEDGER TABLE ============
  const AllTransactionsImageContent = () => {
    if (!exportData) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>{isRTL ? 'جاري التحميل...' : 'Loading...'}</div>;
    const { ledger, opening_balance } = exportData;
    const finalBalance = ledger?.length > 0 ? ledger[ledger.length - 1].running_balance : (opening_balance || 0);
    const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '';
    const periodLabel = useCustomRange
      ? (isRTL ? `الفترة: من ${fmtD(customStart)} إلى ${fmtD(customEnd)}` : `Period: ${fmtD(customStart)} to ${fmtD(customEnd)}`)
      : (isRTL ? `شهر ${monthNames[filterMonth]} ${filterYear}` : `${monthNames[filterMonth]} ${filterYear}`);
    const TP = 'var(--theme-primary, #0F172A)';
    const TA = 'var(--theme-accent, #D4AF37)';

    const cellStyle = { padding: '6px 10px' };
    const headerStyle = (w, align = 'center') => ({ padding: '8px 10px', textAlign: align, fontWeight: '700', width: w });

    return (
      <div style={{ backgroundColor: '#ffffff', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        {/* Header */}
        <div style={{ backgroundColor: TP, padding: '24px 28px', color: '#fff', borderBottom: `3px solid ${TA}` }} data-testid="ledger-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: '12px', color: TA, margin: '0 0 4px', fontWeight: '700', letterSpacing: '1px' }}>AZIZI.CO</p>
              <p style={{ fontSize: '22px', fontWeight: '800', margin: 0 }}>{selectedClient.name}</p>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', margin: '6px 0 0', fontWeight: '600' }}>{isRTL ? 'كشف حساب' : 'Account Statement'}</p>
              <p style={{ fontSize: '14px', color: '#fff', margin: '2px 0 0', fontWeight: '800', display: 'inline-block', padding: '3px 10px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.12)', border: `1px solid ${TA}` }} dir="auto" data-testid="ledger-period-label">{periodLabel}</p>
            </div>
            <div style={{ textAlign: isRTL ? 'left' : 'right' }}>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>{isRTL ? 'الرصيد النهائي' : 'Final Balance'}</p>
              <p style={{ fontSize: '28px', fontWeight: '800', color: finalBalance >= 0 ? '#34d399' : TA, margin: '2px 0 0', direction: 'ltr' }}>{finalBalance?.toLocaleString()} <span style={{ fontSize: '12px' }}>{isRTL ? 'درهم' : 'AED'}</span></p>
            </div>
          </div>
        </div>
        {/* Table */}
        {ledger?.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ backgroundColor: TP, color: TA, borderBottom: `2px solid ${TA}` }}>
                <th style={headerStyle('3%', isRTL ? 'right' : 'left')}>#</th>
                <th style={headerStyle('15%', isRTL ? 'right' : 'left')}>{isRTL ? 'الموظف' : 'Employee'}</th>
                <th style={headerStyle('15%', isRTL ? 'right' : 'left')}>{isRTL ? 'المعاملة' : 'Transaction'}</th>
                <th style={headerStyle('11%', isRTL ? 'right' : 'left')}>{isRTL ? 'المنشأة' : 'Company'}</th>
                <th style={headerStyle('9%')}>{isRTL ? 'التاريخ' : 'Date'}</th>
                <th style={headerStyle('7%')}>{isRTL ? 'التكلفة' : 'Cost'}</th>
                <th style={headerStyle('7%')}>{isRTL ? 'المدفوع' : 'Paid'}</th>
                <th style={headerStyle('7%')}>{isRTL ? 'الإجمالي' : 'Total'}</th>
                <th style={headerStyle('7%')}>{isRTL ? 'الحالة' : 'Status'}</th>
                {isAdmin && <th style={headerStyle('7%')} className="no-print" data-testid="ledger-profit-header">{isRTL ? 'المكسب' : 'Profit'}</th>}
                <th style={headerStyle('13%')} className="no-print" data-testid="ledger-docs-header">{isRTL ? 'المستندات' : 'Documents'}</th>
                {isAdmin && <th style={headerStyle('6%')} className="no-print"></th>}
              </tr>
            </thead>
            <tbody>
              {opening_balance !== 0 && (
                <tr style={{ backgroundColor: '#f0f9ff', borderBottom: '2px solid #bae6fd' }}>
                  <td colSpan={6} style={{ ...cellStyle, fontWeight: '700', color: '#0369a1' }}>{isRTL ? 'رصيد مرحّل من الفترة السابقة' : 'Carried Forward Balance'}</td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>-</td>
                  <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '900', direction: 'ltr', fontSize: '12px', color: opening_balance >= 0 ? '#059669' : '#dc2626' }}>{opening_balance?.toLocaleString()}</td>
                  <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '600', color: '#0369a1', fontSize: '10px' }}>{isRTL ? 'مرحّل' : 'C/F'}</td>
                  {isAdmin && <td className="no-print" />}
                  <td className="no-print" />
                  {isAdmin && <td className="no-print" />}
                </tr>
              )}
              {(() => {
                let rowNum = 0;
                return ledger.map((entry, i) => {
                  // ---- DEPOSIT ROW ----
                  if (entry.entry_type === 'deposit') {
                    const isEditingDep = editingDepositId === entry.id;
                    return (
                      <tr key={`d-${i}`} style={{ backgroundColor: '#ecfdf5', borderBottom: '2px solid #a7f3d0' }}>
                        <td colSpan={5} style={{ ...cellStyle, fontWeight: '700', color: '#047857' }}>
                          {entry.is_refund ? '↩️' : '💰'} {entry.notes || (isRTL ? 'إيداع' : 'Deposit')} - {entry.date ? new Date(entry.date).toLocaleDateString('en-GB') : ''}
                        </td>
                        <td style={{ ...cellStyle, textAlign: 'center' }}>-</td>
                        {/* Deposit amount - editable */}
                        <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '900', color: '#059669', direction: 'ltr', fontSize: '12px' }}>
                          {isEditingDep ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                              <input type="number" value={editDepositAmount} onChange={e => setEditDepositAmount(e.target.value)}
                                style={{ fontSize: '11px', border: '1px solid #059669', borderRadius: '4px', padding: '2px 4px', width: '75px', textAlign: 'center', fontWeight: '800' }}
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveDepositAmount(entry.id); if (e.key === 'Escape') setEditingDepositId(null); }}
                                autoFocus />
                              <button onClick={() => handleSaveDepositAmount(entry.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><Check style={{ width: 12, height: 12, color: '#059669' }} /></button>
                              <button onClick={() => setEditingDepositId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><X style={{ width: 12, height: 12, color: '#dc2626' }} /></button>
                            </span>
                          ) : (
                            <span onClick={() => { if (isAdmin && entry.id) { setEditingDepositId(entry.id); setEditDepositAmount(String(entry.amount || '')); } }}
                              style={{ cursor: isAdmin ? 'pointer' : 'default', borderBottom: isAdmin ? '1px dashed #a7f3d0' : 'none' }}>
                              +{(entry.amount || 0).toLocaleString()}
                            </span>
                          )}
                        </td>
                        <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '900', direction: 'ltr', fontSize: '12px', color: (entry.running_balance || 0) >= 0 ? '#059669' : '#dc2626' }}>{(entry.running_balance || 0).toLocaleString()}</td>
                        <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '600', color: '#047857', fontSize: '10px' }}>{isRTL ? 'إيداع' : 'Deposit'}</td>
                        {isAdmin && <td className="no-print" />}
                        <td className="no-print" />
                        {isAdmin && (
                          <td style={{ ...cellStyle, textAlign: 'center' }} className="no-print">
                            {entry.id && !entry.is_refund && (
                              <button onClick={() => handleDeleteDeposit(entry.id)} title={isRTL ? 'حذف' : 'Delete'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                                <Trash2 style={{ width: 13, height: 13, color: '#dc2626' }} />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  }
                  // ---- TRANSACTION ROW ----
                  rowNum++;
                  const isCancelled = entry.status === 'cancelled';
                  const bgColor = isCancelled ? '#fef2f2' : (rowNum % 2 === 0 ? '#fafbfc' : '#fff');
                  const isEditingPrice = editingId === entry.id && editField === 'price';
                  const isEditingDate = editingId === entry.id && editField === 'date';
                  return (
                    <React.Fragment key={`t-${i}`}>
                    <tr style={{ backgroundColor: bgColor, borderBottom: isCancelled ? 'none' : '1px solid #f1f5f9' }}>
                      <td style={{ ...cellStyle, fontWeight: '700', color: '#64748b', fontSize: '10px' }}>{rowNum}</td>
                      <td style={{ ...cellStyle, fontWeight: '600', color: isCancelled ? '#991b1b' : '#334155', textDecoration: isCancelled ? 'line-through' : 'none' }}>
                        {entry.employee_name 
                          ? entry.employee_name 
                          : entry.company_name 
                            ? <span style={{ color: '#2563eb', fontStyle: 'italic' }}>{isRTL ? 'معاملة منشأة' : 'Company Tx'} - {entry.company_name}</span>
                            : '-'}
                      </td>
                      <td style={{ ...cellStyle, color: isCancelled ? '#991b1b' : '#334155', textDecoration: isCancelled ? 'line-through' : 'none' }}>{translateSubType(entry.sub_type, isRTL) || entry.sub_type}</td>
                      <td style={{ ...cellStyle, color: '#64748b', fontSize: '10px' }}>{entry.company_name}</td>
                      {/* Date */}
                      <td style={{ ...cellStyle, textAlign: 'center', color: '#64748b', direction: 'ltr', fontSize: '10px' }}>
                        {isEditingDate ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                            <input type="date" value={editValue} onChange={e => setEditValue(e.target.value)}
                              style={{ fontSize: '10px', border: '1px solid #D4AF37', borderRadius: '4px', padding: '2px 4px', width: '110px' }}
                              onKeyDown={e => { if (e.key === 'Enter') handleEditSave(entry.id); if (e.key === 'Escape') { setEditingId(null); setEditField(null); } }}
                              autoFocus />
                            <button onClick={() => handleEditSave(entry.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><Check style={{ width: 12, height: 12, color: '#059669' }} /></button>
                            <button onClick={() => { setEditingId(null); setEditField(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><X style={{ width: 12, height: 12, color: '#dc2626' }} /></button>
                          </span>
                        ) : (
                          <span onClick={() => !isCancelled && isAdmin && startEdit(entry.id, 'date', entry.date)}
                            style={{ cursor: !isCancelled && isAdmin ? 'pointer' : 'default', borderBottom: !isCancelled && isAdmin ? '1px dashed #cbd5e1' : 'none' }}>
                            {entry.date ? new Date(entry.date).toLocaleDateString('en-GB') : '-'}
                          </span>
                        )}
                      </td>
                      {/* Price */}
                      <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '800', color: isCancelled ? '#dc2626' : '#b45309', direction: 'ltr' }}>
                        {isEditingPrice ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                            <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)}
                              style={{ fontSize: '11px', border: '1px solid #D4AF37', borderRadius: '4px', padding: '2px 4px', width: '70px', textAlign: 'center', fontWeight: '800' }}
                              onKeyDown={e => { if (e.key === 'Enter') handleEditSave(entry.id); if (e.key === 'Escape') { setEditingId(null); setEditField(null); } }}
                              autoFocus />
                            <button onClick={() => handleEditSave(entry.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><Check style={{ width: 12, height: 12, color: '#059669' }} /></button>
                            <button onClick={() => { setEditingId(null); setEditField(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><X style={{ width: 12, height: 12, color: '#dc2626' }} /></button>
                          </span>
                        ) : (
                          <span onClick={() => !isCancelled && isAdmin && startEdit(entry.id, 'price', entry.price)}
                            style={{ cursor: !isCancelled && isAdmin ? 'pointer' : 'default', borderBottom: !isCancelled && isAdmin ? '1px dashed #cbd5e1' : 'none' }}>
                            {(entry.price || 0).toLocaleString()}
                          </span>
                        )}
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '800', color: '#059669', direction: 'ltr', whiteSpace: 'nowrap' }} title={isCancelled ? (isRTL ? 'مبلغ مسترد إلى رصيد العميل' : 'Refunded to client balance') : undefined} data-testid={isCancelled ? `ledger-refund-${entry.id}` : undefined}>
                        {isCancelled ? <span>↩ +{(entry.price || 0).toLocaleString()}</span> : '-'}
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '900', direction: 'ltr', fontSize: '11px', color: (entry.running_balance || 0) >= 0 ? '#059669' : '#dc2626' }}>{(entry.running_balance || 0).toLocaleString()}</td>
                      <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '600', fontSize: '10px', color: statusColor[entry.status] || '#64748b' }}>
                        {entry.is_company_tx && isAdmin && !isCancelled ? (<>
                          <select value={entry.status || 'pending'} data-testid={`company-tx-status-${entry.id}`} className="no-print"
                            onChange={(e) => handleUpdateCompanyTxStatus(entry.id, e.target.value)}
                            style={{ fontSize: '10px', fontWeight: '700', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '2px 6px', cursor: 'pointer', backgroundColor: entry.status === 'completed' ? '#ecfdf5' : '#fffbeb', color: statusColor[entry.status] || '#64748b' }}>
                            <option value="pending">{isRTL ? 'قيد الإجراء' : 'Pending'}</option>
                            <option value="completed">{isRTL ? 'مكتملة' : 'Completed'}</option>
                            <option value="cancelled">{isRTL ? 'إلغاء المعاملة' : 'Cancel transaction'}</option>
                          </select>
                          <span className="print-only" style={{ display: 'none' }}>{statusLabelMap(entry.status, isRTL)}</span>
                        </>) : statusLabelMap(entry.status, isRTL)}
                      </td>
                      {isAdmin && (
                        <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '800', direction: 'ltr', fontSize: '11px', color: isCancelled || entry.actual_paid_amount == null ? '#cbd5e1' : ((entry.price || 0) - entry.actual_paid_amount) >= 0 ? '#059669' : '#dc2626' }} className="no-print" data-testid={`ledger-profit-${entry.id}`}>
                          {isCancelled ? '-' : entry.actual_paid_amount == null ? '—' : ((entry.price || 0) - entry.actual_paid_amount).toLocaleString()}
                        </td>
                      )}
                      <td style={{ ...cellStyle, textAlign: 'center' }} className="no-print">
                        {!isCancelled && entry.id ? (
                          <TxDocuments txId={entry.id} kind={entry.is_company_tx ? 'company' : 'employee'} documents={entry.documents || {}}
                            isAdmin={isAdmin} headers={headers} isRTL={isRTL} onChanged={updateEntryDocuments} />
                        ) : <span style={{ color: '#cbd5e1' }}>-</span>}
                      </td>
                      {isAdmin && (
                        <td style={{ ...cellStyle, textAlign: 'center' }} className="no-print">
                          {!isCancelled && entry.id && (
                            <button onClick={() => handleDeleteTransaction(entry.id, entry.is_company_tx)} title={isRTL ? 'حذف' : 'Delete'}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                              <Trash2 style={{ width: 13, height: 13, color: '#dc2626' }} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                    </React.Fragment>
                  );
                });
              })()}
              {isAdmin && (() => {
                const txs = (ledger || []).filter(e => e.entry_type === 'transaction' && e.status !== 'cancelled');
                const priced = txs.filter(e => e.actual_paid_amount != null);
                const totalProfit = priced.reduce((s, e) => s + ((e.price || 0) - e.actual_paid_amount), 0);
                const missing = txs.length - priced.length;
                return (
                  <tr className="no-print" style={{ backgroundColor: '#fefce8', borderTop: `2px solid ${TA}` }} data-testid="ledger-profit-total-row">
                    <td colSpan={9} style={{ ...cellStyle, fontWeight: '800', color: TP, textAlign: isRTL ? 'right' : 'left' }}>
                      {isRTL ? 'إجمالي المكسب للفترة' : 'Total profit for period'}
                      {missing > 0 && <span style={{ fontSize: '10px', fontWeight: '600', color: '#b45309', marginInlineStart: '8px' }}>({isRTL ? `${missing} معاملة بدون تكلفة فعلية — الرقم غير نهائي` : `${missing} transaction(s) without actual cost — not final`})</span>}
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '900', direction: 'ltr', fontSize: '12px', color: totalProfit >= 0 ? '#059669' : '#dc2626' }} data-testid="ledger-profit-total">{totalProfit.toLocaleString()}</td>
                    <td colSpan={2} />
                  </tr>
                );
              })()}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '40px 28px', textAlign: 'center', color: '#94a3b8' }}>
            <p style={{ fontSize: '14px', margin: 0 }}>{isRTL ? 'لا توجد معاملات في هذه الفترة' : 'No transactions in this period'}</p>
          </div>
        )}
        {/* Transfer bar */}
        {finalBalance < 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 28px', backgroundColor: TP, borderTop: `3px solid ${TA}` }}>
            <span style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>{isRTL ? 'المبلغ المطلوب تحويله' : 'Amount to Transfer'}</span>
            <span style={{ fontSize: '24px', fontWeight: '800', color: TA, direction: 'ltr' }}>{Math.abs(finalBalance).toLocaleString()} <span style={{ fontSize: '12px' }}>{isRTL ? 'درهم' : 'AED'}</span></span>
          </div>
        )}
        {/* Receipt summary + signatures (print only) */}
        {(() => {
          const txs = (ledger || []).filter(e => e.entry_type === 'transaction' && e.status !== 'cancelled');
          const deps = (ledger || []).filter(e => e.entry_type === 'deposit');
          const totalTx = txs.reduce((s, e) => s + (e.price || 0), 0);
          const totalDep = deps.reduce((s, e) => s + (e.amount || 0), 0);
          const cur = isRTL ? 'درهم' : 'AED';
          const line = (label, val, color) => (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px dashed #e2e8f0', fontSize: '12px' }}>
              <span style={{ color: '#64748b', fontWeight: '600' }}>{label}</span>
              <span style={{ fontWeight: '800', color, direction: 'ltr' }}>{val.toLocaleString()} {cur}</span>
            </div>
          );
          return (
            <div className="print-only" style={{ display: 'none', padding: '16px 28px 8px', backgroundColor: '#fff', borderTop: `2px solid ${TP}` }} data-testid="print-receipt-summary">
              <div style={{ maxWidth: '420px' }}>
                  <p style={{ fontSize: '13px', fontWeight: '800', color: TP, margin: '0 0 8px' }}>{isRTL ? 'ملخص الإيصال' : 'Receipt Summary'}</p>
                  {opening_balance !== 0 && line(isRTL ? 'رصيد مرحّل' : 'Carried forward', opening_balance || 0, (opening_balance || 0) >= 0 ? '#059669' : '#dc2626')}
                  {line(isRTL ? `إجمالي المعاملات (${txs.length})` : `Total transactions (${txs.length})`, totalTx, '#b45309')}
                  {line(isRTL ? `إجمالي الإيداعات (${deps.length})` : `Total deposits (${deps.length})`, totalDep, '#059669')}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', fontSize: '14px' }}>
                    <span style={{ fontWeight: '800', color: TP }}>{isRTL ? 'الرصيد النهائي' : 'Final balance'}</span>
                    <span style={{ fontWeight: '900', color: finalBalance >= 0 ? '#059669' : '#dc2626', direction: 'ltr' }}>{finalBalance?.toLocaleString()} {cur}</span>
                  </div>
              </div>
              <p style={{ fontSize: '10px', color: '#94a3b8', margin: '12px 0 0', textAlign: 'center' }}>{isRTL ? 'هذا الإيصال صادر إلكترونياً من نظام AZIZI.CO ويُعتبر إثباتاً للمعاملات المذكورة أعلاه' : 'This receipt is generated electronically by AZIZI.CO and serves as proof of the transactions listed above'}</p>
            </div>
          );
        })()}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 28px', backgroundColor: '#f8fafc', borderTop: `2px solid ${TA}` }}>
          <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>AZIZI.CO · {selectedClient.name} · {periodLabel}</span>
          <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', direction: 'ltr' }}>{isRTL ? 'تاريخ الإصدار: ' : 'Issued: '}{new Date().toLocaleDateString('en-GB')}</span>
        </div>
      </div>
    );
  };

  // ============ CLIENT SELECTION ============
  if (!selectedClient && fixedClientId) {
    return <div className="py-16 text-center text-slate-400 text-sm" data-testid="statement-empty">{loading ? (isRTL ? 'جاري التحميل...' : 'Loading...') : (isRTL ? 'لا يوجد حساب مرتبط بهذا المستخدم' : 'No account linked to this user')}</div>;
  }
  if (!selectedClient) {
    return (
      <div className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'} data-testid="work-plans-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{isRTL ? 'خطط العمل' : 'Work Plans'}</h1>
            <p className="text-sm text-slate-400 mt-0.5">{isRTL ? 'اختر العميل لعرض المعاملات' : 'Select a client to view transactions'}</p>
          </div>
          {isAdmin && onCreatePlan && (
            <Button onClick={onCreatePlan} className="gap-1.5 bg-[#D4AF37] hover:bg-[#c4a030] text-white" data-testid="create-plan-btn">
              <Plus className="w-4 h-4" /> {isRTL ? 'خطة جديدة' : 'New Plan'}
            </Button>
          )}
        </div>
        {loading ? <p className="text-center text-slate-400 py-8">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {clients.map(client => {
              const cPlans = plans.filter(p => p.client_id === client.id && (p.steps || []).length > 0);
              if (cPlans.length === 0) return null;
              const total = cPlans.reduce((s, p) => s + (p.steps || []).filter(st => st.status !== 'cancelled').reduce((sum, st) => sum + (st.price || 0), 0), 0);
              return (
                <Card key={client.id} className="p-4 cursor-pointer hover:shadow-md transition-shadow border-slate-200 hover:border-[#D4AF37]"
                  onClick={() => setSelectedClient(client)} data-testid={`client-select-${client.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#0F172A] flex items-center justify-center text-white font-bold text-sm">{client.name?.charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{client.name}</p>
                      <p className="text-xs text-slate-400">{cPlans.length} {isRTL ? 'خطة' : 'plans'} | <span dir="ltr">{total.toLocaleString()} AED</span></p>
                    </div>
                    {isRTL ? <ChevronLeft className="w-4 h-4 text-slate-300" /> : <ChevronRight className="w-4 h-4 text-slate-300" />}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ============ CLIENT TRANSACTIONS VIEW ============
  const totalStepsPrice = Object.values(selectedSteps).reduce((s, p) => s + p, 0);

  return (
    <div className="space-y-3" dir={isRTL ? 'rtl' : 'ltr'} data-testid="work-plans-page">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-900">{isRTL ? 'كشف حساب' : 'Statement'}: {selectedClient.name}</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {isRTL ? 'كل المعاملات' : 'All Transactions'} - {useCustomRange ? '' : monthNames[filterMonth] + ' ' + filterYear}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            {!useCustomRange && (
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1">
                <button onClick={() => { setFilterMonth(m => m === 0 ? 11 : m - 1); if (filterMonth === 0) setFilterYear(y => y - 1); }} className="text-slate-400 hover:text-slate-700 text-xs px-1">{'<'}</button>
                <span className="text-xs font-medium text-slate-700 min-w-[80px] text-center">{monthNames[filterMonth]} {filterYear}</span>
                <button onClick={() => { setFilterMonth(m => m === 11 ? 0 : m + 1); if (filterMonth === 11) setFilterYear(y => y + 1); }} className="text-slate-400 hover:text-slate-700 text-xs px-1">{'>'}</button>
              </div>
            )}
            {useCustomRange && (
              <div className="flex items-center gap-1">
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="text-xs border border-slate-200 rounded px-1.5 py-1" />
                <span className="text-xs text-slate-400">-</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="text-xs border border-slate-200 rounded px-1.5 py-1" />
              </div>
            )}
            <button onClick={() => setUseCustomRange(v => !v)} className="text-xs text-slate-500 hover:text-slate-700 underline">
              {useCustomRange ? (isRTL ? 'شهري' : 'Monthly') : (isRTL ? 'مدة محددة' : 'Custom')}
            </button>
          </div>
          {isAdmin && (
            <>
              <Button size="sm" onClick={() => { setShowAddForm(v => !v); if (!showAddForm) { setShowDepositForm(false); setShowCompanyTxForm(false); } }}
                className={`gap-1.5 text-xs h-8 ${showAddForm ? 'bg-slate-600 hover:bg-slate-700' : 'bg-[#D4AF37] hover:bg-[#c4a030]'} text-white`} data-testid="toggle-add-plan-btn">
                {showAddForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                {isRTL ? (showAddForm ? 'إغلاق' : 'إضافة خطة') : (showAddForm ? 'Close' : 'Add Plan')}
              </Button>
              <Button size="sm" onClick={() => { setShowCompanyTxForm(v => !v); if (!showCompanyTxForm) { setShowAddForm(false); setShowDepositForm(false); } }}
                className={`gap-1.5 text-xs h-8 ${showCompanyTxForm ? 'bg-slate-600 hover:bg-slate-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`} data-testid="toggle-company-tx-btn">
                {showCompanyTxForm ? <X className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
                {isRTL ? (showCompanyTxForm ? 'إغلاق' : 'معاملة منشأة') : (showCompanyTxForm ? 'Close' : 'Company Tx')}
              </Button>
              <Button size="sm" onClick={() => { setShowDepositForm(v => !v); if (!showDepositForm) { setShowAddForm(false); setShowCompanyTxForm(false); } }}
                className={`gap-1.5 text-xs h-8 ${showDepositForm ? 'bg-slate-600 hover:bg-slate-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`} data-testid="toggle-deposit-btn">
                {showDepositForm ? <X className="w-3.5 h-3.5" /> : <Wallet className="w-3.5 h-3.5" />}
                {isRTL ? (showDepositForm ? 'إغلاق' : 'إيداع رصيد') : (showDepositForm ? 'Close' : 'Deposit')}
              </Button>
            </>
          )}
          <Button size="sm" onClick={handlePrint} className="gap-1.5 text-xs bg-[#0F172A] hover:bg-[#1e293b] h-8" data-testid="print-client-plans-btn"><Printer className="w-3.5 h-3.5" /> {isRTL ? 'طباعة' : 'Print'}</Button>
          {!fixedClientId && <Button variant="outline" size="sm" onClick={() => { setSelectedClient(null); resetAddForm(); setShowDepositForm(false); }} className="text-xs h-8" data-testid="back-to-clients-btn">{isRTL ? 'رجوع' : 'Back'}</Button>}
        </div>
      </div>

      {/* ===== INLINE ADD PLAN - EXCEL STYLE ===== */}
      {showAddForm && (
        <div className="border border-slate-200 rounded-xl bg-white" style={{ overflow: 'visible' }} data-testid="inline-add-plan-form">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: '#fef3c7' }}>
                <th style={{ padding: '8px 12px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', color: '#92400e', fontSize: '11px', width: '30%' }}>{isRTL ? 'الموظف' : 'Employee'}</th>
                <th style={{ padding: '8px 12px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', color: '#92400e', fontSize: '11px', width: '25%' }}>{isRTL ? 'نوع الخطة' : 'Plan Type'}</th>
                <th style={{ padding: '8px 12px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', color: '#92400e', fontSize: '11px', width: '30%' }}>{isRTL ? 'المعاملات' : 'Steps'}</th>
                <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '700', color: '#92400e', fontSize: '11px', width: '15%' }}>{isRTL ? 'إجراء' : 'Action'}</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderTop: '1px solid #f1f5f9' }}>
                {/* Employee search cell */}
                <td style={{ padding: '8px 12px', verticalAlign: 'top' }}>
                  <div className="relative">
                    <input type="text"
                      value={selectedEmp ? selectedEmp.name : empSearch}
                      onChange={e => { setEmpSearch(e.target.value); setSelectedEmp(null); setEmpDropdownOpen(true); }}
                      onFocus={() => { if (!selectedEmp) setEmpDropdownOpen(true); }}
                      onClick={() => { if (selectedEmp) { setSelectedEmp(null); setEmpSearch(''); setEmpDropdownOpen(true); } }}
                      placeholder={isRTL ? 'ابحث بالاسم...' : 'Search...'}
                      data-testid="inline-plan-employee-search"
                      className="w-full h-8 text-sm border border-slate-200 rounded px-2 bg-white focus:border-[#D4AF37] focus:outline-none"
                    />
                    {empDropdownOpen && !selectedEmp && (
                      <div className="absolute z-50 top-9 inset-x-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {filteredEmps.length === 0 ? (
                          <p className="text-xs text-slate-400 p-3">{isRTL ? 'لا نتائج' : 'No results'}</p>
                        ) : filteredEmps.slice(0, 15).map(emp => (
                          <button key={emp.id} onClick={() => { setSelectedEmp(emp); setEmpSearch(''); setEmpDropdownOpen(false); }}
                            className="w-full text-start px-3 py-2 text-sm hover:bg-amber-50 transition-colors border-b border-slate-50 last:border-0"
                            data-testid={`emp-option-${emp.id}`}>
                            <span className="font-semibold text-slate-800">{emp.name}</span>
                            {emp.company_name && <span className="text-[10px] text-slate-400 mx-1">({emp.company_name})</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                {/* Plan type cell */}
                <td style={{ padding: '8px 12px', verticalAlign: 'top' }}>
                  <select value={formMainType} onChange={e => { setFormMainType(e.target.value); setSelectedSteps({}); }} data-testid="inline-plan-main-type"
                    className="w-full h-8 text-sm border border-slate-200 rounded px-2 bg-white focus:border-[#D4AF37] focus:outline-none">
                    <option value="">{isRTL ? '-- اختر --' : '-- Select --'}</option>
                    {Object.entries(WORKFLOW_STEPS).filter(([, v]) => !v.company).map(([key, val]) => (
                      <option key={key} value={key}>{isRTL ? val.ar : val.en}</option>
                    ))}
                  </select>
                </td>
                {/* Steps cell */}
                <td style={{ padding: '8px 12px', verticalAlign: 'top' }} colSpan={formMainType ? 1 : 2}>
                  {formMainType && availableSteps.length > 0 ? (
                    <div className="space-y-1">
                      {availableSteps.map(step => {
                        const isSelected = selectedSteps[step.sub_type] !== undefined;
                        return (
                          <div key={step.sub_type} className="flex items-center gap-2">
                            <input type="checkbox" checked={isSelected} onChange={() => toggleStep(step)}
                              className="w-3.5 h-3.5 accent-[#D4AF37] cursor-pointer" data-testid={`step-check-${step.sub_type}`} />
                            <span className={`text-xs flex-1 ${isSelected ? 'font-bold text-slate-900' : 'text-slate-500'}`}>
                              {isRTL ? step.ar : step.en}
                            </span>
                            {isSelected && (
                              <input type="number" value={selectedSteps[step.sub_type] || ''}
                                onChange={e => setSelectedSteps(prev => ({ ...prev, [step.sub_type]: parseFloat(e.target.value) || 0 }))}
                                placeholder="0" data-testid={`step-price-${step.sub_type}`}
                                className="w-[70px] h-6 text-xs text-center font-bold border border-slate-200 rounded bg-white focus:border-[#D4AF37] focus:outline-none" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 py-1">{isRTL ? 'اختر نوع الخطة أولاً' : 'Select plan type first'}</p>
                  )}
                </td>
                {/* Action cell */}
                {formMainType && (
                  <td style={{ padding: '8px 12px', verticalAlign: 'top', textAlign: 'center' }}>
                    <div className="space-y-2">
                      {totalStepsPrice > 0 && (
                        <p className="text-xs font-bold text-slate-700" dir="ltr">{totalStepsPrice.toLocaleString()} {isRTL ? 'درهم' : 'AED'}</p>
                      )}
                      <Button size="sm" onClick={handleAddPlan} disabled={submitting || Object.keys(selectedSteps).length === 0}
                        data-testid="inline-plan-submit"
                        className="gap-1 text-xs bg-[#D4AF37] hover:bg-[#c4a030] text-white h-7 w-full disabled:opacity-40">
                        <Plus className="w-3 h-3" /> {isRTL ? 'إضافة' : 'Add'}
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ===== DEPOSIT FORM ===== */}
      {showDepositForm && (
        <div className="border border-emerald-300 rounded-xl bg-emerald-50/30 p-4" data-testid="inline-deposit-form">
          <div className="flex items-center gap-3 flex-wrap">
            <input type="number" value={depositAmount} onChange={e => setDepositAmount(e.target.value)}
              placeholder={isRTL ? 'المبلغ' : 'Amount'} data-testid="deposit-amount"
              className="h-9 text-sm border border-slate-200 rounded-lg px-3 bg-white w-[120px] font-bold focus:border-emerald-400 focus:outline-none" />
            <input type="date" value={depositDate} onChange={e => setDepositDate(e.target.value)}
              data-testid="deposit-date"
              className="h-9 text-sm border border-slate-200 rounded-lg px-3 bg-white focus:border-emerald-400 focus:outline-none" />
            <input type="text" value={depositNotes} onChange={e => setDepositNotes(e.target.value)}
              placeholder={isRTL ? 'ملاحظات (اختياري)' : 'Notes (optional)'} data-testid="deposit-notes"
              className="h-9 text-sm border border-slate-200 rounded-lg px-3 bg-white flex-1 min-w-[150px] focus:border-emerald-400 focus:outline-none" />
            <Button size="sm" onClick={handleAddDeposit} disabled={depositSubmitting} data-testid="deposit-submit"
              className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white h-9">
              <Wallet className="w-3.5 h-3.5" /> {isRTL ? 'إيداع' : 'Deposit'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowDepositForm(false); setDepositAmount(''); setDepositNotes(''); }} className="text-xs h-9">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* ===== COMPANY TRANSACTION FORM ===== */}
      {showCompanyTxForm && (
        <div className="border border-blue-300 rounded-xl bg-blue-50/30 p-4" data-testid="inline-company-tx-form">
          <div className="flex items-center gap-3 flex-wrap">
            <select value={compTxCompanyId} onChange={e => setCompTxCompanyId(e.target.value)} data-testid="company-tx-company"
              className="h-9 text-sm border border-slate-200 rounded-lg px-3 bg-white min-w-[160px] focus:border-blue-400 focus:outline-none">
              <option value="">{isRTL ? '-- اختر المنشأة --' : '-- Select Company --'}</option>
              {clientCompanies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <input type="text" value={compTxName} onChange={e => setCompTxName(e.target.value)}
              placeholder={isRTL ? 'اسم المعاملة' : 'Transaction name'} data-testid="company-tx-name"
              className="h-9 text-sm border border-slate-200 rounded-lg px-3 bg-white min-w-[160px] flex-1 focus:border-blue-400 focus:outline-none" />
            <input type="number" value={compTxPrice} onChange={e => setCompTxPrice(e.target.value)}
              placeholder={isRTL ? 'المبلغ' : 'Amount'} data-testid="company-tx-price"
              className="h-9 text-sm border border-slate-200 rounded-lg px-3 bg-white w-[120px] font-bold focus:border-blue-400 focus:outline-none" />
            <input type="date" value={compTxDate} onChange={e => setCompTxDate(e.target.value)} data-testid="company-tx-date"
              className="h-9 text-sm border border-slate-200 rounded-lg px-3 bg-white focus:border-blue-400 focus:outline-none" />
            <Button size="sm" onClick={handleAddCompanyTx} disabled={compTxSubmitting} data-testid="company-tx-submit"
              className="gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white h-9">
              <Building2 className="w-3.5 h-3.5" /> {isRTL ? 'إضافة' : 'Add'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowCompanyTxForm(false); setCompTxCompanyId(''); setCompTxName(''); setCompTxPrice(''); }} className="text-xs h-9">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* ===== LEDGER ===== */}
      <div ref={captureRef} className="rounded-xl overflow-hidden border border-slate-200 shadow-sm" dir={isRTL ? 'rtl' : 'ltr'}>
        <AllTransactionsImageContent />
      </div>
    </div>
  );
};

export default AllPlansImage;
